<template lang="pug">
branch-list.branch-management(
  ref='template',
  academyNamePlaceholder='塾名',
  branchNamePlaceholder='教室名',
  branchCodePlaceholder='教室コード',
  modalTitle='教室登録',
  modalBranchNamePlaceholder='教室名',
  modalBranchCodePlaceholder='教室コード',
  :userRole='userRole',
  :currentLink='currentLink',
  :sideMenuLinkEvent='sideMenuLinkEvent',
  :logoutEvent='logoutEvent',
  :breadcrumbs='breadcrumbs',
  :setRegisterDataEvent='setRegisterDataEvent',
  :tabHeadingEvent='tabHeadingEvent',
  :searchEvent='searchEvent',
  :downloadEvent='downloadEvent',
  :uploadEvent='uploadEvent',
  :sort='sort',
  :branchDatas='branchDatas',
  :deleteEvent='deleteEvent',
  :setEditDataEvent='setEditDataEvent',
  :pagenationStatus='pagenationStatus',
  :pagenationEvent='pagenationEvent',
  :branchEditData='branchEditData',
  :academySelectOptions='academySelectOptions',
  :modalKeepButtonEvent='modalKeepButtonEvent',
  :modalErrorMessages='modalErrorMessages',
  @sort-changed='sortChanged'
)
  template(#sideMenuSeparateText) 設定
  template(#logoutText) サインアウト
  template(#breadcrumbButtonLabel) 教室登録
  template(#searchButtonText) 絞り込む
  template(#downloadIconLabel) ダウンロード
  template(#uploadIconLabel) アップロード
  template(#academyNameHeader) 塾名
  template(#branchNameHeader) 教室名
  template(#branchCodeHeader) 教室コード
  template(#updatedDateHeader) 最終更新日
  template(#editHeader) 編集
  template(#popupRemoveText) 削除
  template(#popupEditText) 編集
  template(#modalAcademyNameLabel) 塾名
  template(#modalAcademyNameDisabledLabel) 塾名（編集不可）
  template(#modalBranchNameLabel) 教室名
  template(#modalBranchCodeLabel) 教室コード
  template(#modalBranchCodeDisabledLabel) 教室コード（編集不可）
  template(#modalKeepText) 保存
</template>

<script lang="ts">
import { Component, Mixins, Ref } from 'vue-property-decorator'
import LoginMethods from '@/mixins/LoginMethods'
import BranchesApi from '@/mixins/api/BranchesApi'
import AcademiesApi from '@/mixins/api/AcademiesApi'
import BranchList from '@/components/templates/manager/BranchList.vue'
import { breadcrumbType } from '@/models/breadcrumbModel'
import { BranchListType, BranchListSortType, BranchEditType, BranchSearchParamsType } from '@/models/branchListModel'
import { InputItem } from '@/models/inputItem'
import { PaginationStatus } from '@/models/paginationModel'
import { LinkSetting, SideMenuSettings } from '@/const/SideMenuSettings'
import { UserRoles } from '@/const/UserRoles'
import { InputItemPlaceholderSetting } from '@/const/DisplaySettings'
import { SortSettings } from '@/const/SortSettings'
import { ManagerInputUtils } from '@/utils/ManagerInputUtils'
import { BranchManagementUtils } from '@/utils/BranchManagementUtils'

/**
 * システム管理者・教室管理画面
 */
@Component({
  components: {
    BranchList,
  },
})
export default class BranchManagement extends Mixins(LoginMethods, BranchesApi, AcademiesApi) {
  // テンプレートへのアクセス
  @Ref() template!: BranchList

  // パンくず
  private breadcrumbs: breadcrumbType[] = [
    {
      label: '塾・教室管理',
    },
  ]

  // システム管理者権限
  private userRole: UserRoles = UserRoles.ADMIN

  // サイドメニューの現在リンク
  private currentLink: LinkSetting = SideMenuSettings.MANAGER_ACADEMY

  // ソート情報
  private sort: BranchListSortType | null = null

  // 教室一覧表示情報
  private branchDatas: BranchListType[] = []

  // ページネーションの状態
  private pagenationStatus: PaginationStatus | null = null

  // モーダルに表示する教室編集情報
  private branchEditData: BranchEditType | null = null

  // 塾選択オプション
  private academySelectOptions: InputItem[] = []

  // モーダルに表示するエラーメッセージ
  private modalErrorMessages: string[] = []

  // 現在の検索パラメーター
  private currentSearchParams: BranchSearchParamsType | null = null

  // 現在のページ
  private get currentPage(): number {
    return this.pagenationStatus?.page || 1
  }

  // 初期表示処理
  private async mounted() {
    this.$loading.start()
    // デフォルトソート値をセットして1ページ目を検索する
    this.setDefaultSort()
    await this.search(1)
    // 塾一覧API呼び出し
    const apiResponse = await this.getAcademies()
    // 塾選択オプションに成型する
    this.academySelectOptions = ManagerInputUtils.buildAcademyInputItems(
      ManagerInputUtils.buildPlaceholder(InputItemPlaceholderSetting.MANAGER_ACADEMY),
      apiResponse
    )
    this.$loading.complete()
  }

  // デフォルトソート値をセット
  private setDefaultSort() {
    this.sort = {
      academyName: SortSettings.ASC,
      branchName: SortSettings.ASC,
      branchCode: SortSettings.ASC,
      updatedDate: SortSettings.ASC,
    }
  }

  // 検索ボタン押下イベント
  private async searchEvent() {
    this.$loading.start()
    // 全チェックボックス、ソートを初期化
    this.template.checkedAll = false
    this.setDefaultSort()
    // 1ページ目を検索
    await this.search(1)
    this.$loading.complete()
  }

  // ページ遷移イベント
  private async pagenationEvent(page: number) {
    this.$loading.start()
    // 指定されたページを検索
    await this.search(page)
    this.$loading.complete()
  }

  // ソート変更時イベント
  private async sortChanged(sort: BranchListSortType) {
    this.$loading.start()
    // ソートを更新
    this.sort = sort
    // 現在のページを検索
    await this.search(this.currentPage)
    this.$loading.complete()
  }

  // 検索処理
  private async search(page: number) {
    if (!this.sort) {
      return
    }
    // 教室一覧情報取得API呼び出し
    this.currentSearchParams = {
      page: page,
      academyName: this.template.academyName,
      branchName: this.template.branchName,
      branchCode: this.template.branchCode,
      academyNameSort: this.sort.academyName?.code || null,
      branchNameSort: this.sort.branchName.code,
      branchCodeSort: this.sort.branchCode.code,
      updatedAtSort: this.sort.updatedDate.code,
    }
    const apiResponse = await this.searchBranches(this.currentSearchParams, this.userRole)
    // 表示情報に成型する
    this.branchDatas = BranchManagementUtils.formatBranchDatas(apiResponse)
    this.pagenationStatus = BranchManagementUtils.getPagenationStatus(apiResponse)
    // チェックボックスのチェック状態をセット
    this.setCheckedBranchIds()
  }

  // チェックボックスのチェック状態をセットする処理
  private setCheckedBranchIds() {
    if (this.template.checkedAll) {
      // 全チェックボックスがチェックされている場合、全データのチェックボックスをチェックする
      this.template.checkedBranchIds = this.branchDatas.map((branchData) => {
        return branchData.id
      })
    } else {
      // チェックされていない場合は全てを未チェックにする
      this.template.checkedBranchIds = []
    }
  }

  // ダウンロードイベント
  private async downloadEvent() {
    if (this.currentSearchParams === null) {
      return
    }
    // 対象チェックボックスにチェックがない場合はダウンロードを行わない
    if (this.template.checkedBranchIds.length === 0) {
      alert('ダウンロード対象をチェックしてください')
      return
    }
    // ダウンロード処理
    this.$loading.start()
    // チェックボックスがチェックされた教室情報を絞り込みに使用する（全選択時は絞り込み不要）
    const branchIds: number[] = this.template.checkedAll ? [] : this.template.checkedBranchIds
    try {
      // 直近の検索パラメーターを用いてエクセルデータを取得する
      const exportData = await this.exportBranchExcel(this.currentSearchParams, branchIds, this.userRole)
      // aタグを生成し、hrefにデータを設定してclickを実行することでファイルをダウンロード
      const fileLink = document.createElement('a')
      fileLink.href = URL.createObjectURL(exportData)
      fileLink.download = '教室一覧.xlsx'
      fileLink.click()
      this.$loading.complete()
    } catch {
      alert('エラーが発生しました')
    }
  }

  // アップロードイベント
  private async uploadEvent(file: File) {
    this.$loading.start()
    try {
      await this.importBranchExcel(file, this.userRole)
      alert('取込みが完了しました')
      // 再検索する
      await this.search(this.currentPage)
      this.$loading.complete()
    } catch (e: any) {
      alert(e.response.data.message.message)
    }
  }

  // サイドメニュー遷移イベント
  private sideMenuLinkEvent(link: LinkSetting) {
    // 設定されたリンク先ページへ遷移
    this.$router.push({ name: `Admin${link.name}` })
  }

  // ログアウトイベント
  private logoutEvent() {
    this.logout()
    // ログインページへ遷移
    this.$router.push({ name: 'AdminLogin' })
  }

  // 見出しタブ押下イベント
  private tabHeadingEvent(name: string) {
    // リンク先ページへ遷移
    this.$router.push({ name: `Admin${name}` })
  }

  // 新規登録情報を設定するイベント
  private setRegisterDataEvent() {
    // エラーメッセージを初期化する
    this.modalErrorMessages = []
    // 初期値を設定
    this.branchEditData = BranchManagementUtils.buildAdminBranchEditData()
  }

  // 編集データを取得して設定するイベント
  private async setEditDataEvent(id: number) {
    // エラーメッセージを初期化する
    this.modalErrorMessages = []
    this.$loading.start()
    // 教室情報取得API呼び出し
    const apiResponse = await this.getBranchData(id, this.userRole)
    // 編集情報に成型する
    this.branchEditData = BranchManagementUtils.buildAdminBranchEditData(apiResponse)
    this.$loading.complete()
  }

  // モーダルの保存ボタン押下時のイベント
  private async modalKeepButtonEvent(): Promise<void> {
    if (!this.branchEditData) {
      return
    }
    // エラーメッセージを初期化する
    this.modalErrorMessages = []
    this.$loading.start()
    try {
      if (this.branchEditData.id) {
        // branchEditDataにIDがある場合は更新処理
        await this.updateBranchData(this.branchEditData, this.userRole)
      } else {
        // ない場合は新規登録
        await this.createBranchData(this.branchEditData, this.userRole)
      }
      // 再検索する
      await this.search(this.currentPage)
      this.$loading.complete()
    } catch (e: any) {
      // エラー発生時はエラーメッセージを設定する
      this.modalErrorMessages = [e.response.data.message]
    }
  }

  // 削除イベント
  private async deleteEvent(id: number): Promise<void> {
    this.$loading.start()
    try {
      await this.deleteBranchData(id, this.userRole)
      // 再検索する
      await this.search(this.currentPage)
      this.$loading.complete()
    } catch (e: any) {
      // エラー時はエラーメッセージをアラート
      alert(e.response.data.message)
    }
  }
}
</script>
